import React from 'react';
import moment from 'moment';
import { types as sdkTypes } from './sdkLoader';
import toPairs from 'lodash/toPairs';
import { FormattedMessage } from 'react-intl';
import { isValidYtunnus } from './businessId';
const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;
export const NAME_MIN_LENGTH = 2;

// Restricted terms for display name validation.
// Match against normalized input (lowercased, whitespace/punctuation stripped).
// Use substring matching — not exact match — to catch compounds like "freedomlysupport".
export const RESTRICTED_NAME_TERMS = [
  // Brand & platform
  'freedomly',
  'sharetribe',

  // Support & help
  'support',
  'helpdesk',
  'customerservice',
  'servicedesk',
  'assistance',
  'helpcenter',
  'tuki',
  'asiakastuki',
  'asiakaspalvelu',
  'palvelupiste',
  'neuvonta',
  'tukitiimi',
  'ohjekeskus',

  // Administration & authority
  'admin',
  'administrator',
  'sysadmin',
  'moderator',
  'superadmin',
  'webmaster',
  'yllapito',
  'yllapitaja',
  'paakayttaja',
  'moderaattori',
  'valvoja',

  // Roles & titles
  'founder',
  'perustaja',
  'omistaja',
  'toimitusjohtaja',
  'johtaja',
  'paallikko',
  'henkilokunta',
  'tyontekija',
  'virkailija',
  'edustaja',
  'asiamies',
  'koordinaattori',

  // Security & trust signals
  'security',
  'verified',
  'trusted',
  'official',
  'certified',
  'authorized',
  'safepay',
  'escrow',
  'turvallisuus',
  'vahvistettu',
  'luotettu',
  'virallinen',
  'sertifioitu',
  'valtuutettu',

  // System & technical
  'system',
  'noreply',
  'mailer',
  'billing',
  'payment',
  'invoice',
  'jarjestelma',
  'botti',
  'automaatti',
  'ilmoitus',
  'halytys',
  'tiedotus',
  'laskutus',
  'maksu',
  'lasku',

  // Fraud-specific
  'refund',
  'verify',
  'confirm',
  'unlock',
  'suspend',
  'dispute',
  'compliance',
  'recovery',
  'palautus',
  'vahvista',
  'varmista',
  'keskeyta',
  'tarkistus',
  'reklamaatio',
];

const linksMessage = <FormattedMessage id="Validators.links" />;
const contactInformationMessage = <FormattedMessage id="Validators.contactInformation" />;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const phoneNumberRequired = message => value => {
  if (required(value) !== undefined) {
    return PH_NO_RE.test(value) ? VALID : message;
  }
};

export const textEditorValidation = message => value => {
  let error = '';

  if (value && hasLink(value)) {
    error = linksMessage;
  }
  if (value && (PH_NO_RE.test(value) || PH_NO_RE2.test(value) || AT_RE.test(value))) {
    error = contactInformationMessage;
  }
  if (error !== '') {
    return error;
  } else {
    return VALID;
  }
};

// Validates that a rich-text HTML value only contains the given tags.
// Quill strips disallowed formats while editing, but pasted or legacy content
// can still contain other tags — this surfaces them as a field error in real
// time and keeps them from being saved.
export const allowedHtmlTagsValidation = allowedTags => value => {
  if (!value) {
    return VALID;
  }
  const tagRe = /<\/?([a-z][a-z0-9]*)/gi;
  const disallowed = new Set();
  let match;
  while ((match = tagRe.exec(value)) !== null) {
    const tag = match[1].toLowerCase();
    if (!allowedTags.includes(tag)) {
      disallowed.add(tag);
    }
  }
  if (disallowed.size === 0) {
    return VALID;
  }
  return (
    <FormattedMessage
      id="Validators.disallowedFormatting"
      values={{ tags: Array.from(disallowed).join(', ') }}
    />
  );
};

export const noLinks = message => value => {
  if (!value) {
    return VALID;
  }
  return hasLink(value) ? message || linksMessage : VALID;
};

export const optional = message => value => {
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

const NAME_RE = /^[\p{L}\p{M}\s'’\-]+$/u;

export const validName = message => value => {
  if (!value || typeof value !== 'string') {
    return VALID;
  }
  return NAME_RE.test(value) ? VALID : message;
};

// A full name: at least two whitespace-separated names, where the first
// and last name each contain at least two letters.
export const fullNameValid = message => value => {
  if (!value || typeof value !== 'string') {
    return message;
  }
  const parts = value.trim().split(/\s+/);
  if (parts.length < 2) {
    return message;
  }
  const letterCount = part => (part.match(/\p{L}/gu) || []).length;
  return letterCount(parts[0]) >= 2 && letterCount(parts[parts.length - 1]) >= 2 ? VALID : message;
};

export const nameRestricted = (message, restrictedTerms) => value => {
  if (!value || typeof value !== 'string') {
    return VALID;
  }
  const normalized = value.toLowerCase().replace(/[^a-z0-9]/g, '');
  const hasRestricted = restrictedTerms.some(term => normalized.includes(term));
  return hasRestricted ? message : VALID;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const AT_RE = /@/i;
//eslint-disable-next-line
const PH_NO_RE = /\s?(\+\d{1,3}\s?)?1?\-?\.?\s?\(?\d{1,3}\)?[\s.-]?\d{3}[\s.-]?\d{4,5}/;
const PH_NO_RE2 = /(\+\d{1,3}\s?)?((\(\d{3}\)\s?)|(\d{3})(\s|-?))(\d{3}(\s|-?))(\d{4})(\s?(([E|e]xt[:|.|]?)|x|X)(\s?\d+))?/;
// Link detection.
//
// Catches URLs while avoiding false positives on prose people actually type:
// dates (1.2.2024), abbreviations (U.S.A), sentence-end-no-space (Hyvä.Huomenna),
// version strings (v1.2.3), and filenames (readme.md).
//
// Three branches:
//   1. Protocol / www — always matched, unambiguous.
//   2. Bare lowercase domain — catches "example.com", "bit.ly", new/obscure TLDs.
//      Case-sensitive lowercase, left word-boundary, first label must start with
//      a letter (kills numeric dates), TLD must be followed by a URL terminator.
//   3. Mixed-case bare domain with known TLD — catches "Google.com",
//      "Freedomly.fi", "Acme.org". TLD must still be lowercase (prevents
//      "Ok.Se"-style false positives where a capitalized TLD-like suffix is
//      really a Finnish/English word). TLD list is intentionally conservative:
//      missing new TLDs is recoverable by typing with "https://" or "www." or
//      lowercase; catching Finnish/English common-word suffixes is not.
const KNOWN_TLDS =
  'com|org|net|edu|gov|mil|int|info|biz|io|co|ai|app|dev|me|xyz|tech|online|shop|blog|cloud|digital|news|live|tv|fi|se|no|dk|de|uk|eu|nl|fr|es|pl|ca|au|jp|br';
const PROTO_OR_WWW_RE = /\bhttps?:\/\/\S+|\bwww\.[A-Za-z0-9-]+\.[A-Za-z]{2,24}\b/;
const BARE_DOMAIN_LC_RE = /(?:^|[\s(\[<"'])[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)*\.[a-z]{2,24}(?=[\s/?#:)"'.,!]|$)/;
const BARE_DOMAIN_KNOWN_TLD_RE = new RegExp(
  `(?:^|[\\s(\\[<"'])[A-Za-z][A-Za-z0-9-]*(?:\\.[A-Za-z0-9-]+)*\\.(?:${KNOWN_TLDS})(?=[\\s/?#:)"'.,!]|$)`
);
const LINK_RE = new RegExp(
  `${PROTO_OR_WWW_RE.source}|${BARE_DOMAIN_LC_RE.source}|${BARE_DOMAIN_KNOWN_TLD_RE.source}`
);

// Common file extensions that would otherwise look like bare two-label domains
// in chat ("readme.md", "file.txt"). Used to suppress filename false positives.
const FILE_EXT_TAIL_RE = /\.(txt|pdf|jpe?g|png|gif|docx?|xlsx?|pptx?|csv|zip|rar|mp[34]|mov|json|js|ts|css|html?|svg|md)(?=[\s)"'.,!]|$)/i;

// Strip HTML tags so rich-text editor output (Quill emits e.g. "<p>google.com</p>")
// is matched the same as plain text — the tag characters would otherwise break
// the left word-boundary in the bare-domain branches.
const stripHtml = value => value.replace(/<[^>]*>/g, ' ');

const hasLinkInPlainText = text => {
  const match = text.match(LINK_RE);
  if (!match) return false;
  const looksLikeFilename =
    FILE_EXT_TAIL_RE.test(match[0]) && !/^https?:\/\/|^www\./i.test(match[0].trimStart());
  if (looksLikeFilename) {
    return hasLinkInPlainText(text.slice(match.index + match[0].length));
  }
  return true;
};

export const hasLink = value => {
  if (typeof value !== 'string' || !value) {
    return false;
  }
  return hasLinkInPlainText(stripHtml(value));
};

// `required` for rich-text editor values. Quill reports a cleared editor as
// "<p><br></p>" (a non-empty string), so strip the markup before checking.
export const requiredRichText = message => value =>
  required(message)(typeof value === 'string' ? stripHtml(value) : value);

const HTML_ENTITIES = { '&nbsp;': ' ', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'" };

// Visible text of rich-text editor output: tags removed, common entities
// decoded to one character each, inner whitespace kept.
export const richTextToText = html => {
  if (typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&(nbsp|lt|gt|quot|#39);/g, entity => HTML_ENTITIES[entity])
    .replace(/&amp;/g, '&');
};

export const richTextLength = html => richTextToText(html).length;

export const richTextMaxLength = (message, max) => value =>
  richTextLength(value) > max ? message : VALID;

// Leading and trailing whitespace doesn't count towards a minimum
export const richTextMinLength = (message, min) => value =>
  richTextToText(value).trim().length < min ? message : VALID;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = moment();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = now.diff(moment(age), 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);

// Finnish Y-tunnus: format and check digit live in the shared CommonJS module.
export const ytunnusValid = message => value => {
  if (!value) return message;
  return isValidYtunnus(value) ? VALID : message;
};

// Finnish HETU (henkilötunnus). Format: DDMMYY±NNNC where ± is the century
// marker (+ for 1800s; -, Y, X, W, V, U for 1900s; A, B, C, D, E, F for
// 2000s — the letters other than A were added in 2023), NNN is a 3-digit
// serial, and C is a check character from the table
// "0123456789ABCDEFHJKLMNPRSTUVWXY".
const HETU_RE = /^(\d{2})(\d{2})(\d{2})([+\-A-FU-Y])(\d{3})([0-9A-Z])$/;
const HETU_CHECK_TABLE = '0123456789ABCDEFHJKLMNPRSTUVWXY';
const hetuChecksumValid = value => {
  if (typeof value !== 'string') return false;
  const m = value.toUpperCase().match(HETU_RE);
  if (!m) return false;
  const [, dd, mm, yy, , nnn, c] = m;
  const day = parseInt(dd, 10);
  const month = parseInt(mm, 10);
  if (day < 1 || day > 31) return false;
  if (month < 1 || month > 12) return false;
  const num = parseInt(`${dd}${mm}${yy}${nnn}`, 10);
  const expected = HETU_CHECK_TABLE[num % 31];
  return expected === c;
};

export const hetuValid = message => value => {
  if (!value) return message;
  return hetuChecksumValid(value) ? VALID : message;
};

export const finnishPostalCode = message => value => {
  if (!value) return message;
  return /^\d{5}$/.test(value) ? VALID : message;
};

// ISO-3166 alpha-2 country code. Two uppercase letters.
export const iso2CountryCode = message => value => {
  if (!value) return message;
  return /^[A-Z]{2}$/.test(value) ? VALID : message;
};

// IBAN: ISO 13616. 2-letter country code, 2 check digits, up to 30
// alphanumeric BBAN chars (15–34 total). Accepts user-typed spaces.
// e.g. "FI21 1234 5600 0007 85", "DE89 3704 0044 0532 0130 00"
const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
export const normalizeIban = value =>
  typeof value === 'string' ? value.replace(/\s+/g, '').toUpperCase() : value;

// Mod-97 check: move first 4 chars to the end, replace each letter with
// two digits (A=10 … Z=35), then the resulting integer must be ≡ 1 (mod 97).
const ibanMod97 = iban => {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const expanded = rearranged.replace(/[A-Z]/g, ch => ch.charCodeAt(0) - 55);
  let remainder = 0;
  for (let i = 0; i < expanded.length; i += 1) {
    remainder = (remainder * 10 + Number(expanded[i])) % 97;
  }
  return remainder;
};

// Boolean IBAN check: format + mod-97 checksum. Accepts user-typed
// spaces and lowercase input. Country-agnostic.
export const isValidIban = value => {
  if (typeof value !== 'string') return false;
  const normalized = normalizeIban(value);
  return IBAN_RE.test(normalized) && ibanMod97(normalized) === 1;
};

// Two-letter country prefix of an IBAN, or null when there isn't one.
export const ibanCountryCode = value => {
  const normalized = normalizeIban(value);
  return typeof normalized === 'string' && /^[A-Z]{2}/.test(normalized)
    ? normalized.slice(0, 2)
    : null;
};

// IBAN prefixes of the SEPA countries: EU-27 + IS, LI, NO + AD, CH, GB,
// GI, MC, SM, VA. Gibraltar participates via the UK but has its own prefix.
// prettier-ignore
export const SEPA_IBAN_COUNTRIES = [
  'AD', 'AT', 'BE', 'BG', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
  'FR', 'GB', 'GI', 'GR', 'HR', 'HU', 'IE', 'IS', 'IT', 'LI', 'LT', 'LU',
  'LV', 'MC', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'SM', 'VA',
];

export const isSepaIban = value => SEPA_IBAN_COUNTRIES.includes(ibanCountryCode(value));

export const ibanValidation = message => value => {
  if (!value) return message;
  return isValidIban(value) ? VALID : message;
};

export const packageValidation = (packageName, name, description, price, intl) => {
  const errors = {};
  const desc = description?.replace('<p><br></p>', '');

  const validateDescription = required(
    intl.formatMessage({
      id: 'EditListingPricingForm.descriptionRequired',
    })
  );

  const validateName = required(
    intl.formatMessage({
      id: 'EditListingPricingForm.nameRequired',
    })
  );

  const validatePrice = required(
    intl.formatMessage({
      id: 'EditListingPricingForm.priceRequired',
    })
  );

  if (name) {
    errors[`${packageName}Description`] = validateDescription(desc);
    errors[`${packageName}Price`] = validatePrice(price);
  }

  if (desc) {
    errors[`${packageName}Name`] = validateName(name);
    errors[`${packageName}Price`] = validatePrice(price);
  }

  if (price) {
    errors[`${packageName}Name`] = validateName(name);
    errors[`${packageName}Description`] = validateDescription(desc);
  }

  return errors;
};
